import { type CaregiverRole } from '@libraries/typescript/api_client';

const ROLE_DISPLAY_NAMES: Record<CaregiverRole, string> = {
    rn: 'RN',
    lpn: 'LPN',
    cna: 'CNA',
    companion: 'Companion',
    hha: 'HHA',
    pt: 'PT',
    ot: 'OT',
    st: 'ST',
    msw: 'MSW',
    cpi: 'CPI',
    dsw: 'DSW',
    np: 'NP',
    pca: 'PCA',
    hcss: 'HCSS',
    acd: 'ACD',
    other: 'Other',
};

const ROLE_FULL_NAMES: Record<CaregiverRole, string> = {
    rn: 'Registered Nurse',
    lpn: 'Licensed Practical Nurse',
    cna: 'Certified Nursing Assistant',
    companion: 'Companion',
    hha: 'Home Health Aide',
    pt: 'Physical Therapist',
    ot: 'Occupational Therapist',
    st: 'Speech Therapist',
    msw: 'Medical Social Worker',
    cpi: 'Certified Patient Instructor',
    dsw: 'Direct Support Worker',
    np: 'Nurse Practitioner',
    pca: 'Personal Care Assistant',
    hcss: 'Home and Community Support Services',
    acd: 'Alternative Competency Demonstration',
    other: 'Other',
};

const ROLE_COLORS: Record<
    CaregiverRole,
    { className: string; style: React.CSSProperties }
> = {
    rn: {
        className: 'text-blue-800 border-0',
        style: { backgroundColor: '#DBEAFE' },
    },
    lpn: {
        className: 'text-indigo-800 border-0',
        style: { backgroundColor: '#E0E7FF' },
    },
    cna: {
        className: 'text-purple-800 border-0',
        style: { backgroundColor: '#EDE9FE' },
    },
    hha: {
        className: 'text-violet-800 border-0',
        style: { backgroundColor: '#F3E8FF' },
    },
    pt: {
        className: 'text-green-800 border-0',
        style: { backgroundColor: '#D1FAE5' },
    },
    ot: {
        className: 'text-emerald-800 border-0',
        style: { backgroundColor: '#D1FAE5' },
    },
    st: {
        className: 'text-teal-800 border-0',
        style: { backgroundColor: '#CCFBF1' },
    },
    msw: {
        className: 'text-orange-800 border-0',
        style: { backgroundColor: '#FED7AA' },
    },
    cpi: {
        className: 'text-amber-800 border-0',
        style: { backgroundColor: '#FEF3C7' },
    },
    dsw: {
        className: 'text-yellow-800 border-0',
        style: { backgroundColor: '#FEF3C7' },
    },
    np: {
        className: 'text-cyan-800 border-0',
        style: { backgroundColor: '#CFFAFE' },
    },
    pca: {
        className: 'text-sky-800 border-0',
        style: { backgroundColor: '#BAE6FD' },
    },
    hcss: {
        className: 'text-magenta-800 border-0',
        style: { backgroundColor: '#F9A8D4' },
    },
    acd: {
        className: 'text-fuchsia-800 border-0',
        style: { backgroundColor: '#F0ABFC' },
    },
    other: {
        className: 'text-gray-800 border-0',
        style: { backgroundColor: '#F3F4F6' },
    },
    companion: {
        className: 'text-rose-800 border-0',
        style: { backgroundColor: '#FFE4E6' },
    },
};

/**
 * Get the display name for a caregiver role (e.g., "rn" -> "RN")
 */
export function getRoleDisplayName(role: CaregiverRole): string {
    return ROLE_DISPLAY_NAMES[role];
}

/**
 * Get the full name for a caregiver role (e.g., "rn" -> "Registered Nurse")
 */
export function getRoleFullName(role: CaregiverRole): string {
    return ROLE_FULL_NAMES[role];
}

/**
 * Get the color styling for a caregiver role badge
 */
export function getRoleColor(role: CaregiverRole): {
    className: string;
    style: React.CSSProperties;
} {
    return ROLE_COLORS[role];
}
